"use client";

import { useId, useState } from "react";
import Image from "next/image";

export type Leader = {
  name: string;
  role: string;
  org: string;
  /** Path under /public, e.g. "/team/ajay-dubey.jpg". Falls back to initials when absent. */
  photo?: string;
  /** Full LinkedIn profile URL. The badge is omitted entirely when absent — no dead links. */
  linkedin?: string;
  /** Shown collapsed. Keep to ~3 lines so the three cards' toggles share a baseline. */
  teaser: string;
  /** Revealed on "Read full bio". One string per paragraph. */
  bio: string[];
};

function initials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

export default function LeadershipCard({ leader }: { leader: Leader }) {
  const [open, setOpen] = useState(false);
  const bioId = useId();

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 text-center transition-shadow duration-200 hover:shadow-lg hover:shadow-slate-200/50">
      {/* Avatar — round, small, real photo or initials fallback */}
      {leader.photo ? (
        <Image
          src={leader.photo}
          alt={leader.name}
          width={80}
          height={80}
          className="mx-auto h-20 w-20 rounded-full object-cover ring-2 ring-white shadow-md shadow-slate-300/40"
        />
      ) : (
        <div
          aria-hidden="true"
          className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-paper-dark font-display text-lg font-bold tracking-wide text-slate-400 ring-2 ring-white shadow-md shadow-slate-300/40"
        >
          {initials(leader.name)}
        </div>
      )}

      <h3 className="font-display text-lg font-bold text-ink tracking-tight mt-4">
        {leader.name}
      </h3>
      {/*
        Reserve two lines of role so a title that wraps ("Co-founder & Chief
        Technology Officer") doesn't push its card's divider and toggle below
        its neighbours. The height is reserved on the role+org BLOCK rather
        than the role alone — reserving it on the role would leave a floating
        gap between role and org whenever the role fits on one line.
      */}
      <div className="min-h-[4rem]">
        <p className="text-xs font-medium text-brand-600 mt-1.5 leading-relaxed">
          {leader.role}
        </p>
        <p className="text-xs text-slate-400 mt-0.5">{leader.org}</p>
      </div>

      {leader.linkedin && (
        <a
          href={leader.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${leader.name} on LinkedIn`}
          className="mt-3 inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#0A66C2] text-white transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-md hover:shadow-[#0A66C2]/40"
        >
          <svg className="h-3.5 w-3.5 fill-current" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
          </svg>
        </a>
      )}

      {/*
        min-h keeps the three "Read full bio" buttons on one baseline while
        collapsed. Stretching the cards instead would make every card grow when
        one bio expands. Set to the 6-line worst case (137px) so the reserve is
        always the binding constraint — the toggles then stay aligned even if a
        teaser wraps to one more line than expected on another browser.
      */}
      <p className="mt-4 min-h-[8.6rem] border-t border-slate-100 pt-4 text-justify hyphens-auto text-sm leading-relaxed text-slate-600">
        {leader.teaser}
      </p>

      {open && (
        <div id={bioId} className="text-justify hyphens-auto animate-fade-in">
          {leader.bio.map((para, i) => (
            <p key={i} className="mt-3 text-sm leading-relaxed text-slate-600">
              {para}
            </p>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={open ? bioId : undefined}
        className="mt-4 inline-flex items-center gap-1.5 border-b-2 border-[var(--color-gold)] pb-0.5 text-xs font-bold text-ink transition-colors hover:text-brand-700"
      >
        {open ? "Show less" : "Read full bio"}
        <svg
          className={`h-3 w-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2.5}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>
    </div>
  );
}
